import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import PDFModal from "./PDFModal";

const SummaryReport = ({ fromDate = "", toDate = "" }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  const buildQuery = (type) => {
    const params = new URLSearchParams({ type });
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    return params.toString();
  };

  const fetchData = async (type) => {
    setLoading(true);
    try {
      const res = await api.get(`/export/summary?${buildQuery(type)}`);
      const base64Data = res?.data?.data;
      if (!base64Data) {
        throw new Error("Something went wrong while generating report");
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      if (type === "pdf") {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        const blob = new Blob([byteNumbers], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setIsPdfModalOpen(true);
      } else if (type === "excel") {
        const blob = new Blob([byteNumbers], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${res?.data?.file_name}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast({
          title: "Success",
          description: "Excel file downloaded successfully",
          variant: "success",
        });
      }
    } catch (error) {
      toast({
        title: "Failed",
        description:
          error.response?.data?.message ||
          error.message ||
          "Something went wrong while generating report",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold mb-3">Summary Report</h3>
          <div className="flex gap-2">
            <Button
              onClick={() => fetchData("pdf")}
              disabled
              variant="outline"
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" /> Export PDF
            </Button>
            <Button
              onClick={() => fetchData("excel")}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" /> Export Excel
            </Button>
          </div>
        </div>
      </div>
      <PDFModal
        isOpen={isPdfModalOpen}
        onClose={closePdfModal}
        pdfUrl={pdfUrl}
        title="Report"
      />
    </div>
  );
};

export default SummaryReport;
